import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { catchError, of, take } from 'rxjs';
import { TransformedResponse } from '../../../shared/models/dryer-checkbox-selection.dto';
import { NotificationService, NotificationType } from '../../../shared/services/notification.service';
import { ProductsService } from '../../../shared/services/products.service';

@Component({
  selector: 'app-product-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
})
export class SideNavComponent implements OnInit {
  dryersType: TransformedResponse[] = [];
  allComplete: boolean = false;

  // Suppresses per-checkbox BehaviorSubject emissions during bulk initialization so
  // the debounce in ProductContentComponent only fires once after everything is set up.
  private isInitializing = false;

  constructor(
    private productService: ProductsService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.productService.setLoading(true);
    this.productService
      .getDryerTypes()
      .pipe(
        catchError((err) => {
          this.notificationService.showNotification(NotificationType.Error, `Error while loading dryer types: ${err.message}`);
          return of([]);
        }),
      )
      .subscribe((dryerData: TransformedResponse[]) => {
        // Defer ALL mutations to the next microtask so Angular's current change-detection
        // pass completes before dryersType is updated — prevents NG0100.
        Promise.resolve().then(() => {
          this.dryersType = dryerData;

          if (this.dryersType.length === 0) {
            this.productService.setCheckboxesLoaded(true);
            this.productService.setLoading(false);
            this.cdr.detectChanges();
            return;
          }

          this.isInitializing = true;
          this.initializeCheckboxes();

          this.productService
            .getSelectedDryerGroup()
            .pipe(take(1))
            .subscribe((dryerGroup: string) => {
              if (dryerGroup) {
                this.selectCheckboxByGrandParentName(dryerGroup);
              }
              this.isInitializing = false;
              // Single emission after all checkbox state is settled.
              this.emitSelectionChange();
              this.cdr.detectChanges();
            });
        });
      });
  }

  private initializeCheckboxes(): void {
    this.dryersType.forEach((grandparent) => {
      grandparent.isChecked = false;
      this.onCheckboxChange(grandparent);
      grandparent?.children.forEach((parent) => {
        parent.isChecked = false;
        this.onCheckboxChange(parent);
        parent?.children.forEach((child) => {
          child.isChecked = false;
          this.onCheckboxChange(child);
        });
      });
    });
  }

  onCheckboxChange(selectedItem: TransformedResponse): void {
    // Clear previously selected checkboxes when any checkbox changes
    this.productService.clearSelectedCheckboxes();

    this.updateChildren(selectedItem);
    this.updateParents();
    this.emitSelectionChange();
  }

  private selectCheckboxByGrandParentName(dryerGroup: string): void {
    this.dryersType.forEach((grandparent) => {
      if (grandparent.checkboxLabel === dryerGroup) {
        grandparent.isChecked = true;
        this.onCheckboxChange(grandparent);
      } else {
        grandparent.isChecked = false;
        this.onCheckboxChange(grandparent);
      }
    });
  }

  updateChildren(selectedItem: TransformedResponse): void {
    if (selectedItem?.children) {
      selectedItem?.children.forEach((child) => {
        child.isChecked = selectedItem.isChecked;
        child.isIndeterminate = false;
        this.updateChildren(child);
      });
    }
  }

  updateParents(): void {
    this.dryersType.forEach((grandparent) => {
      this.updateParentState(grandparent);
    });
  }

  updateParentState(selectedItem: TransformedResponse): void {
    if (selectedItem?.children?.length) {
      selectedItem.children.forEach((child) => {
        this.updateParentState(child);
      });

      const allChildrenChecked = selectedItem.children.every((child) => child.isChecked);
      const someChildrenChecked = selectedItem.children.some((child) => child.isChecked || child.isIndeterminate);

      selectedItem.isChecked = allChildrenChecked;
      selectedItem.isIndeterminate = !allChildrenChecked && someChildrenChecked;
    } else {
      selectedItem.isIndeterminate = false;
    }
  }

  emitSelectionChange(): void {
    if (this.isInitializing) {
      return;
    }
    const selectedValues = this.dryersType.reduce((acc, grandparent) => {
      grandparent?.children.forEach((parent) => {
        if (parent?.isChecked || parent?.isIndeterminate) {
          const selectedChildren = parent.children
            .filter((child: { isChecked }) => child.isChecked)
            .map((child: { checkboxLabel }) => child.checkboxLabel);
          acc.push({
            grandparent: grandparent.checkboxLabel,
            label: parent.checkboxLabel,
            children: selectedChildren.length ? selectedChildren : null,
          });
        } else if (parent.isChecked) {
          // This handles parents with no child
          acc.push({
            grandparent: grandparent.checkboxLabel, //dryerGroup
            label: parent.checkboxLabel, //dryerType
            children: null, //dryerCategory
          });
        }
      });
      return acc;
    }, []);

    const selectedGrandParents = this.dryersType.reduce((acc: string[], grandParent) => {
      if (grandParent?.children?.length) {
        const allChildrenSelected = grandParent.children.every((parent) => {
          if (!parent?.children || !parent.children.length) {
            return parent.isChecked;
          } else {
            return parent.isChecked && parent.children.every((child) => child.isChecked);
          }
        });

        if (allChildrenSelected) {
          acc.push(grandParent.checkboxLabel);
        }
      }
      return acc;
    }, []);

    // When nothing is selected, clear any previously checked rows in the result tables
    // so the Comparison and Cost Comparison tabs show the "no selection" message immediately.
    if (selectedValues.length === 0) {
      this.productService.clearSelectedCheckboxes();
    }

    // Emit the selected grandparent values to the service
    this.productService.updateSelectedGrandParentValues(selectedGrandParents);
    this.productService.updateSelectedValues(JSON.parse(JSON.stringify(selectedValues)));
    this.productService.setCheckboxesLoaded(true);
    this.productService.setLoading(false);
  }
}
